import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { previewShareToLibrary, shareToLibrary } from '@/lib/api/generated/api';
import type {
  LibraryPieceRef,
  SharePreview,
  ShareResult,
} from '@/lib/api/generated/model';
import { invalidateLibrary } from '@/features/library';

import { workflowKeys } from '../queryKeys';

/** A refused share request: the service's message, and its code when it gave one. */
export class ShareRequestError extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null) {
    super(message);
    this.name = 'ShareRequestError';
    this.code = code;
  }
}

/**
 * Say that a share answer came back unlike the contract, or that the service
 * failed. The person still sees a refusal; this is for whoever has to find out
 * why.
 */
function reportOdd(problem: string, body: unknown): void {
  console.warn(`share to Library: ${problem}`, body);
}

function refusal(
  status: number,
  data: unknown,
  fallback: string,
): ShareRequestError {
  const body = typeof data === 'object' && data !== null ? data : {};
  const message =
    'message' in body && typeof body.message === 'string' && body.message
      ? body.message
      : null;
  const code =
    'error' in body && typeof body.error === 'string' ? body.error : null;
  if (status >= 500 || message === null || code === null) {
    reportOdd(`${String(status)} ${code ?? 'without a code'}`, data);
  }
  return new ShareRequestError(message ?? fallback, code);
}

/** What a share request carries: the ticks, and the pieces left to the Library (spec 212). */
export interface ShareChoice {
  agents: string[];
  skills: string[];
  useLibrary: LibraryPieceRef[];
}

/**
 * What sharing would do with these agents and skills, piece by piece (spec 206).
 *
 * A query rather than a button: the answer is a property of the ticks, so
 * changing them re-asks, and the dialog never shows outcomes for a choice the
 * person has since changed. Sorted keys keep one cache entry per choice.
 */
export function useSharePreview(
  workspaceId: string,
  workflowId: string,
  choice: ShareChoice | null,
) {
  const agents = [...(choice?.agents ?? [])].sort();
  const skills = [...(choice?.skills ?? [])].sort();
  const useLibrary = choice?.useLibrary ?? [];
  const query = useQuery({
    queryKey: workflowKeys.sharePreview(
      workspaceId,
      workflowId,
      agents,
      skills,
      useLibrary,
    ),
    queryFn: async (): Promise<SharePreview> => {
      const response = await previewShareToLibrary(workspaceId, workflowId, {
        agents,
        skills,
        use_library: useLibrary,
      });
      if (response.status === 200) return response.data;
      throw refusal(
        response.status,
        response.data,
        'What sharing would do could not be checked.',
      );
    },
    enabled: choice !== null,
    // The last outcomes stay on their rows while a changed choice is checked
    // again (spec 211 FR-008): badges change, nothing blanks or moves.
    placeholderData: keepPreviousData,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
  const { refetch } = query;
  return {
    preview: query.data,
    isFetching: query.isFetching,
    /** `preview` is the last choice's answer, kept while this one is checked. */
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message,
    errorCode:
      query.error instanceof ShareRequestError ? query.error.code : null,
    refetch,
  };
}

/** Put the workflow and its parts in the Library (spec 206; service spec 164). */
export function useShareToLibrary(workspaceId: string, workflowId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (choice: ShareChoice): Promise<ShareResult> => {
      const response = await shareToLibrary(workspaceId, workflowId, {
        agents: choice.agents,
        skills: choice.skills,
        use_library: choice.useLibrary,
      });
      if (response.status === 200) return response.data;
      throw refusal(response.status, response.data, 'It could not be shared.');
    },
    onSuccess: () => invalidateLibrary(queryClient, workspaceId),
  });
}
